import React, { useState, useEffect, useMemo } from "react"
import { useMutation, useQuery } from "urql"

import { PAYMENT_STATUS_OPTIONS } from "../../../@core/appointment/appointment.model"
import { useFeatureFlags } from "../../../contexts/FeatureFlagContext"
import { useManualBooking } from "../../../contexts/ManualBookingContext"
import { usePractice } from "../../../contexts/PracticeContext"
import { useToast } from "../../../contexts/ToastContext"
import { fetchAppointment } from "../../../utils/fetchAppointment"
import { formatPhone, formatPrice } from "../../../utils/utils"
import AppointmentAdjustments from "../../shared/AppointmentAdjustments"
import ConfirmManualChargeModal from "../../shared/ConfirmManualChargeModal"
import IntakeFormListSection from "../../shared/IntakeFormListSection"
import IntakeFormSelectModal, { MODE_ADD } from "../../shared/IntakeFormSelectModal"
import { Link } from "../../shared/Links"

import {
  PRACTICE_QUERY,
  CREATE_APPOINTMENT_MUTATION,
  CANCEL_APPOINTMENT_MUTATION,
  ACCEPT_APPOINTMENT_MUTATION,
  DECLINE_APPOINTMENT_MUTATION,
  UPDATE_APPOINTMENT_V2_MUTATION,
  RETRY_PACKAGE_PAYMENT_MUTATION
} from "./appointment_graphql"
import AppointmentActions from "./client_event_form/AppointmentActions"
import AppointmentDetails from "./client_event_form/AppointmentDetails"
import AppointmentReview from "./client_event_form/AppointmentReview"
import AppointmentStatus from "./client_event_form/AppointmentStatus"
import ClientInfo from "./client_event_form/ClientInfo"
import ConfirmModals from "./client_event_form/ConfirmModals"
import ErrorBanner from "./client_event_form/ErrorBanner"
import ManualPaymentInvoiceSection from "./client_event_form/ManualPaymentInvoiceSection"
import PaymentAuditLogSection from "./client_event_form/PaymentAuditLogSection"
import { haveIntakeFormChanges } from "./intakeFormChanges"

const CLIENT_INTAKE_SUBMISSIONS_QUERY = `
  query ClientIntakeSubmissionsForAppt($clientId: ID!) {
    client(clientId: $clientId) {
      id
      intakeSubmissions {
        intakeFormId
        intakeFormName
        status
        completedAt
      }
    }
  }
`

const SEND_INTAKE_FORMS_MUTATION = `
  mutation SendIntakeFormsForAppt($clientId: ID!, $intakeFormIds: [ID!]!, $appointmentId: ID) {
    sendIntakeForms(clientId: $clientId, intakeFormIds: $intakeFormIds, appointmentId: $appointmentId) {
      result
      errors
    }
  }
`

const ClientEventForm = ({
  onAppointmentChanged,
  onRescheduleSuccess,
  onAppointmentCancelled,
  onRecurringAppointmentsChanged
}) => {
  const [showReschedule, setShowReschedule] = useState(null)
  const [practitionerNote, setPractitionerNote] = useState("")
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showPackageConfirmModal, setShowPackageConfirmModal] = useState(false)
  const [showMissingCardConfirmModal, setShowMissingCardConfirmModal] = useState(false)
  const [missingCardConfirmMode, setMissingCardConfirmMode] = useState("appointment")
  const [showMissingPhoneConfirmModal, setShowMissingPhoneConfirmModal] = useState(false)
  const [missingPhoneConfirmMode, setMissingPhoneConfirmMode] = useState("appointment")
  const [showPastAppointmentModal, setShowPastAppointmentModal] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [cancelRecurringSeries, setCancelRecurringSeries] = useState(false)
  const [intakeSelectModalOpen, setIntakeSelectModalOpen] = useState(false)
  const [selectedIntakeForms, setSelectedIntakeForms] = useState([])
  const [removedAppointmentFormIds, setRemovedAppointmentFormIds] = useState([])
  const [retryingPackagePayment, setRetryingPackagePayment] = useState(false)
  const [showPackageRetryConfirm, setShowPackageRetryConfirm] = useState(false)
  // { action: "book" | "reschedule", retry: () => void } — set when the backend
  // returns override_required (connected calendar unreachable during verification)
  const [calendarOverridePrompt, setCalendarOverridePrompt] = useState(null)

  const {
    appointment,
    setAppointment,
    services,
    locationId,
    client,
    startsAt,
    endsAt,
    recurringStartTimes,
    recurrencePattern,
    createRecurringAppointments,
    createRecurringAppointmentsFetching,
    resetBookingState,
    appointmentErrors,
    setAppointmentErrors
  } = useManualBooking()

  const [{ fetching: createFetching }, createAppointment] = useMutation(CREATE_APPOINTMENT_MUTATION)
  const [{ fetching: updateV2Fetching }, updateAppointmentV2] = useMutation(UPDATE_APPOINTMENT_V2_MUTATION)
  const [{ fetching: cancelFetching }, cancelAppointment] = useMutation(CANCEL_APPOINTMENT_MUTATION)
  const [{ fetching: acceptFetching }, acceptAppointment] = useMutation(ACCEPT_APPOINTMENT_MUTATION)
  const [{ fetching: declineFetching }, declineAppointment] = useMutation(DECLINE_APPOINTMENT_MUTATION)
  const [, retryPackagePayment] = useMutation(RETRY_PACKAGE_PAYMENT_MUTATION)

  const fetching =
    createFetching ||
    updateV2Fetching ||
    cancelFetching ||
    createRecurringAppointmentsFetching ||
    acceptFetching ||
    declineFetching

  const { showToast } = useToast()
  const { practice, setPracticeServices, setPractice } = usePractice()
  const featureFlags = useFeatureFlags()
  const multipleIntakeFormsEnabled = featureFlags?.multipleIntakeFormsEnabled !== false

  const [{ data: practiceData }] = useQuery({
    query: PRACTICE_QUERY,
    variables: { includePrivate: true, includeAddOns: true },
    requestPolicy: "cache-and-network"
  })

  const [{ data: intakeData }, reexecuteIntake] = useQuery({
    query: CLIENT_INTAKE_SUBMISSIONS_QUERY,
    variables: { clientId: client?.id },
    requestPolicy: "cache-first",
    pause: !client?.id
  })

  const [, sendIntakeForms] = useMutation(SEND_INTAKE_FORMS_MUTATION)

  const intakeSubmissions = intakeData?.client?.intakeSubmissions ?? []
  const appointmentIntakeForms = appointment?.appointmentIntakeForms ?? []
  const appointmentRows = appointmentIntakeForms.filter((row) => !removedAppointmentFormIds.includes(row.intakeFormId))
  const existingFormIds = appointmentRows.map((row) => row.intakeFormId).concat(selectedIntakeForms.map((f) => f.id))
  const completedFormIds = intakeSubmissions.filter((s) => s.status === "completed").map((s) => s.intakeFormId)

  const intakeFormsForList = [
    ...appointmentRows.map((row) => ({
      id: row.intakeFormId,
      name: row.intakeFormName,
      status: row.status,
      completedAt: row.completedAt
    })),
    ...selectedIntakeForms.map((f) => {
      const submission = intakeSubmissions.find((s) => String(s.intakeFormId) === String(f.id))
      return {
        id: f.id,
        name: f.name,
        status: submission?.status ?? "not_sent",
        completedAt: submission?.completedAt ?? null
      }
    })
  ]

  const initialIntakeFormIds = useMemo(
    () => (appointment?.appointmentIntakeForms ?? []).map((row) => row.intakeFormId),
    [appointment?.id, appointment?.appointmentIntakeForms]
  )
  const currentIntakeFormIds = useMemo(() => intakeFormsForList.map((f) => f.id), [intakeFormsForList])
  const hasIntakeFormChanges = !!appointment && haveIntakeFormChanges(initialIntakeFormIds, currentIntakeFormIds)

  const handleAddIntakeForms = (selectedForms) => {
    const newForms = selectedForms.filter((f) => !selectedIntakeForms.some((s) => s.id === f.id))
    setSelectedIntakeForms((prev) => [...prev, ...newForms])
  }

  const handleIntakeFormAction = (actionId, form) => {
    if (actionId === "view" && client?.id && form?.id) {
      if (hasIntakeFormChanges) {
        showToast({ content: "Save your appointment changes before viewing a form.", type: "info" })
        return
      }
      window.location.href = `/portal/clients/${client.id}/intake_form?formId=${form.id}`
      return
    }
    if (actionId === "resend" && client?.id) {
      sendIntakeForms({ clientId: client.id, intakeFormIds: [form.id], appointmentId: appointment?.id }).then((res) => {
        if (res.data?.sendIntakeForms?.result === "success")
          showToast(multipleIntakeFormsEnabled ? "Intake forms sent" : "Intake form sent")
      })
      return
    }
    if (actionId === "remove" && selectedIntakeForms.some((f) => f.id === form.id)) {
      setSelectedIntakeForms((prev) => prev.filter((f) => f.id !== form.id))
      return
    }
    if (actionId === "remove" && appointmentRows.some((row) => row.intakeFormId === form.id)) {
      setRemovedAppointmentFormIds((prev) => [...new Set([...prev, form.id])])
    }
  }

  const sendPendingIntakeForms = (clientId, appointmentId, pendingFormIds, onComplete) => {
    if (!clientId || pendingFormIds.length === 0) {
      onComplete?.()
      return
    }
    sendIntakeForms({
      clientId,
      intakeFormIds: pendingFormIds,
      appointmentId
    }).then((result) => {
      if (result.error) {
        showToast({
          content: multipleIntakeFormsEnabled ? "Error sending intake forms" : "Error sending intake form",
          type: "error"
        })
        onComplete?.()
        return
      }
      if (result.data?.sendIntakeForms?.result === "success") {
        setSelectedIntakeForms([])
        reexecuteIntake({ requestPolicy: "network-only" })
        onComplete?.()
      } else {
        const errors = result.data?.sendIntakeForms?.errors?.join(", ") ?? "Error"
        showToast({
          content: errors,
          type: "error"
        })
        onComplete?.()
      }
    })
  }

  useEffect(() => {
    setSelectedIntakeForms([])
    setRemovedAppointmentFormIds([])
  }, [appointment?.id])

  useEffect(() => {
    if (practiceData?.currentPractice?.services) {
      setPracticeServices(practiceData.currentPractice.services)
    }
    if (practiceData && practiceData?.currentPractice?.bookingAvailability?.availableSlots && !appointment) {
      resetDefaultValues()
      setPractice((prev) => ({ ...prev, bookingAvailability: practiceData.currentPractice.bookingAvailability }))
    }
  }, [practiceData, appointment])

  const resetDefaultValues = () => {
    resetBookingState()
  }

  const handleErrors = (data, successCallback) => {
    if (data?.result === "success") {
      successCallback(data)
    } else {
      setAppointmentErrors(data?.errors?.join() || "Unexpected error. Please contact support if this persists.")
    }
  }

  const createManualAppointment = ({
    acknowledgeMissingCard = false,
    acknowledgeMissingPhone = false,
    acknowledgeCalendarOverride = false,
    tipAmountCents = 0
  } = {}) => {
    let clientId = null
    let newClient = null

    if (client.id) {
      clientId = client.id
    } else {
      newClient = client
    }

    createAppointment({
      appointmentServices: services
        .filter((service) => service.service?.id)
        .map((service) => ({
          amountCents: service.amountCents,
          serviceId: service.service.id,
          saveCustomPrice: service.saveCustomPrice
        })),
      locationId: locationId,
      startsAt,
      endsAt,
      clientId: clientId,
      client: newClient,
      intakeFormIds: intakeFormsForList.map((form) => form.id),
      acknowledgeMissingCard,
      acknowledgeMissingPhone,
      acknowledgeCalendarOverride,
      tipAmountCents
    })
      .then((result) => {
        if (result.error) {
          console.error("Error creating appointment:", result.error) // eslint-disable-line no-console
          setAppointmentErrors(result.error.message || "Failed to create appointment. Please try again.")
          return
        }

        const appointmentData = result.data?.proCreateAppointment
        if (!appointmentData) {
          console.error("No appointment data returned:", result) // eslint-disable-line no-console
          setAppointmentErrors("Unexpected error. Please contact support if this persists.")
          return
        }

        if (appointmentData.result === "override_required") {
          setCalendarOverridePrompt({
            action: "book",
            variant: appointmentData.overrideReason === "conflict" ? "conflict" : "unreachable",
            retry: () =>
              createManualAppointment({
                acknowledgeMissingCard,
                acknowledgeMissingPhone,
                acknowledgeCalendarOverride: true,
                tipAmountCents
              })
          })
          return
        }

        handleErrors(appointmentData, () => {
          const createdClientId = appointmentData.appointment?.clientId ?? client?.id
          const pendingIntakeFormIds = intakeFormsForList
            .filter((form) => form.status === "not_sent")
            .map((form) => form.id)
          sendPendingIntakeForms(createdClientId, appointmentData.appointment?.id, pendingIntakeFormIds, () => {
            resetDefaultValues()
            showToast("Appointment created successfully.")
            onAppointmentChanged(appointmentData.appointment)
          })
        })
      })
      .catch((error) => {
        console.error("Exception creating appointment:", error) // eslint-disable-line no-console
        setAppointmentErrors(error.message || "Failed to create appointment. Please try again.")
      })
  }

  const editManualAppointment = ({ acknowledgeCalendarOverride = false } = {}) => {
    updateAppointmentV2({
      appointmentId: appointment.id,
      appointmentServices: services
        .filter((service) => service.service?.id)
        .map((service) => ({
          amountCents: service.amountCents,
          serviceId: service.service.id,
          saveCustomPrice: service.saveCustomPrice
        })),
      locationId,
      startsAt,
      endsAt,
      practitionerNote,
      intakeFormIds: intakeFormsForList.map((form) => form.id),
      acknowledgeCalendarOverride
    }).then((result) => {
      if (result.data?.updateAppointmentV2?.result === "override_required") {
        setCalendarOverridePrompt({
          action: "reschedule",
          variant: result.data.updateAppointmentV2.overrideReason === "conflict" ? "conflict" : "unreachable",
          retry: () => editManualAppointment({ acknowledgeCalendarOverride: true })
        })
        return
      }

      handleErrors(result.data?.updateAppointmentV2, () => {
        const pendingIntakeFormIds = intakeFormsForList
          .filter((form) => form.status === "not_sent")
          .map((form) => form.id)
        sendPendingIntakeForms(client?.id, appointment?.id, pendingIntakeFormIds, () => {
          showToast("Appointment updated successfully.")
          onAppointmentChanged(result.data?.updateAppointmentV2?.appointment)
        })
      })
    })
  }

  const handleCancelSuccess = () => {
    cancelAppointment({ appointmentId: appointment.id, practitionerNote, cancelRecurringSeries }).then((result) => {
      if (result.data.cancelAppointment.result === "success") {
        setShowCancelModal(false)
        showToast({
          type: "info",
          content: cancelRecurringSeries ? "Your recurring series was cancelled" : "Your appointment was cancelled"
        })
        onAppointmentCancelled(result.data.cancelAppointment.cancelledAppointmentIds)
      } else {
        setShowCancelModal(false)
        setAppointmentErrors(result.data.cancelAppointment.errors.join())
      }
    })
  }

  const promptCalendarOverride = (retry, overrideReason) =>
    setCalendarOverridePrompt({
      action: "book",
      variant: overrideReason === "conflict" ? "conflict" : "unreachable",
      retry
    })

  const handlePackageConfirm = (tipAmountCents = 0) => {
    setShowPackageConfirmModal(false)

    if (recurringStartTimes && recurrencePattern) {
      createRecurringAppointments(
        (appointments) => {
          resetDefaultValues()
          onRecurringAppointmentsChanged(appointments)
        },
        { acknowledgeMissingCard: true, tipAmountCents, onOverrideRequired: promptCalendarOverride }
      )
    } else {
      createManualAppointment({ acknowledgeMissingCard: true, tipAmountCents })
    }
  }

  const handleMissingCardConfirm = () => {
    setShowMissingCardConfirmModal(false)

    if (missingCardConfirmMode === "recurring") {
      createRecurringAppointments(
        (appointments) => {
          resetDefaultValues()
          onRecurringAppointmentsChanged(appointments)
        },
        { acknowledgeMissingCard: true, acknowledgeMissingPhone: true, onOverrideRequired: promptCalendarOverride }
      )
    } else {
      createManualAppointment({ acknowledgeMissingCard: true, acknowledgeMissingPhone: true })
    }
  }

  const handleMissingPhoneConfirm = () => {
    setShowMissingPhoneConfirmModal(false)

    if (missingPhoneConfirmMode === "recurring") {
      createRecurringAppointments(
        (appointments) => {
          resetDefaultValues()
          onRecurringAppointmentsChanged(appointments)
        },
        { acknowledgeMissingPhone: true, acknowledgeMissingCard: true, onOverrideRequired: promptCalendarOverride }
      )
    } else {
      createManualAppointment({ acknowledgeMissingPhone: true, acknowledgeMissingCard: true })
    }
  }

  const handleMissingPhoneRemind = () => {
    if (!client?.id) return
    // Fire-and-forget reminder; booking continues only after Continue & book.
    fetch("/graphql", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({
        query: `mutation($clientId: ID!) {
          sendClientPhoneReminder(clientId: $clientId) { result errors }
        }`,
        variables: { clientId: client.id }
      })
    }).then(async (response) => {
      const json = await response.json()
      const payload = json?.data?.sendClientPhoneReminder
      if (payload?.result === "success") {
        showToast({ content: "Reminder sent", type: "success" })
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not send reminder", type: "error" })
      }
    })
  }

  const handlePastAppointmentConfirm = () => {
    if (pendingAction) {
      pendingAction()
    }
    setShowPastAppointmentModal(false)
    setPendingAction(null)
  }

  const showManualPaymentButton =
    appointment?.paymentStatus !== PAYMENT_STATUS_OPTIONS.PAID && appointment?.amountCents > 0

  const packageRetryEligible =
    appointment?.package?.id &&
    !appointment.package.markPaid &&
    practice.hasPaymentProvider &&
    (appointment.package.portalPaymentOutstanding || appointment.package.paymentStatus === "Unpaid")

  const packageAmountLabel =
    appointment?.package?.amountCents != null
      ? formatPrice(appointment.package.amountCents, appointment.package.amountCurrency || practice.defaultCurrency)
      : null

  const packageClientName =
    client?.firstName || appointment?.client?.firstName || appointment?.user?.firstName || "client"

  const handleRequestPackageRetry = () => {
    if (!appointment?.package?.id || retryingPackagePayment) return
    setShowPackageRetryConfirm(true)
  }

  const handleConfirmPackageRetry = () => {
    if (!appointment?.package?.id || retryingPackagePayment) return

    setRetryingPackagePayment(true)
    retryPackagePayment({ packageId: appointment.package.id }).then(async (res) => {
      const payload = res.data?.retryPackagePayment
      if (payload?.result === "success") {
        showToast("Success! Package payment processed.")
        setShowPackageRetryConfirm(false)
        const refreshed = await fetchAppointment(appointment.instantActionToken)
        if (refreshed) {
          setAppointment(refreshed)
          onAppointmentChanged?.(refreshed)
        }
      } else {
        showToast({
          type: "error",
          content: `Payment failed: ${payload?.errors?.[0] || "Internal server error"}`
        })
      }
      setRetryingPackagePayment(false)
    })
  }

  let clientPhone = formatPhone(client?.phone)
  if (clientPhone) {
    clientPhone = clientPhone.startsWith("+1") || clientPhone.startsWith("1") ? clientPhone : "+1 " + clientPhone
  }

  return (
    <div className={fetching ? "pointer-events-none opacity-25" : ""}>
      {appointment && <AppointmentStatus />}

      {appointment && (
        <PaymentAuditLogSection
          paymentProcessingAuditSummary={appointment.paymentProcessingAuditSummary}
          paymentAuditLogs={appointment.paymentAuditLogs ?? []}
          paymentGatewayReference={appointment.paymentGatewayReference}
          showPackageRetry={packageRetryEligible}
          onRequestPackageRetry={handleRequestPackageRetry}
          retryingPackagePayment={retryingPackagePayment}
          packageAmountLabel={packageAmountLabel}
        />
      )}

      {packageRetryEligible && packageAmountLabel && (
        <ConfirmManualChargeModal
          visible={showPackageRetryConfirm}
          hideModal={() => {
            if (!retryingPackagePayment) setShowPackageRetryConfirm(false)
          }}
          onConfirm={handleConfirmPackageRetry}
          clientName={packageClientName}
          amountLabel={packageAmountLabel}
          chargeSummary={appointment.package?.name || "package purchase"}
          cardBrand={appointment.defaultPaymentMethod?.brand}
          cardLast4={appointment.defaultPaymentMethod?.last4}
          confirmLoading={retryingPackagePayment}
          header="Confirm package charge"
        />
      )}

      {showManualPaymentButton && <ManualPaymentInvoiceSection />}

      <ClientInfo />

      {practice.clientNotesEnabled && client && appointment && (
        <div className="mb-6 flex w-full justify-end">
          <Link href={`/portal/clients/${client?.id}#${appointment.instantActionToken}`}>+ Add appointment notes</Link>
        </div>
      )}

      <AppointmentDetails />

      <AppointmentReview />

      {practice.clientNotesEnabled && client && (
        <div className="mb-6 mt-4">
          <IntakeFormListSection
            forms={intakeFormsForList}
            actionLabel={multipleIntakeFormsEnabled ? "Add a form" : "Add form"}
            onAction={() => setIntakeSelectModalOpen(true)}
            onFormAction={handleIntakeFormAction}
            showActionButton={multipleIntakeFormsEnabled}
            multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
          />
          {multipleIntakeFormsEnabled && (
            <IntakeFormSelectModal
              visible={intakeSelectModalOpen}
              onClose={() => setIntakeSelectModalOpen(false)}
              onSubmit={handleAddIntakeForms}
              existingFormIds={existingFormIds}
              completedFormIds={completedFormIds}
              mode={MODE_ADD}
            />
          )}
        </div>
      )}

      {appointment && appointment.paymentStatus === "Paid" && <AppointmentAdjustments />}

      <ErrorBanner appointmentErrors={appointmentErrors} />

      <AppointmentActions
        fetching={fetching}
        hasIntakeFormChanges={hasIntakeFormChanges}
        onDecline={() => declineAppointment({ appointmentId: appointment.id })}
        removeAppointmentFromCalendar={onAppointmentCancelled}
        onAccept={() => acceptAppointment({ appointmentId: appointment.id })}
        onAppointmentChanged={onAppointmentChanged}
        onCancel={() => setShowCancelModal(true)}
        createManualAppointment={createManualAppointment}
        editManualAppointment={editManualAppointment}
        onRecurringAppointmentsChanged={onRecurringAppointmentsChanged}
        resetBookingState={resetBookingState}
        setShowPackageConfirmModal={setShowPackageConfirmModal}
        setShowMissingCardConfirmModal={setShowMissingCardConfirmModal}
        setMissingCardConfirmMode={setMissingCardConfirmMode}
        setShowMissingPhoneConfirmModal={setShowMissingPhoneConfirmModal}
        setMissingPhoneConfirmMode={setMissingPhoneConfirmMode}
        setShowPastAppointmentModal={setShowPastAppointmentModal}
        setPendingAction={setPendingAction}
      />

      <ConfirmModals
        showReschedule={showReschedule}
        showCancelModal={showCancelModal}
        showPackageConfirmModal={showPackageConfirmModal}
        showMissingCardConfirmModal={showMissingCardConfirmModal}
        missingCardConfirmMode={missingCardConfirmMode}
        showMissingPhoneConfirmModal={showMissingPhoneConfirmModal}
        showPastAppointmentModal={showPastAppointmentModal}
        onRescheduleSuccess={onRescheduleSuccess}
        onCancelSuccess={handleCancelSuccess}
        hideRescheduleModal={() => setShowReschedule(false)}
        hideCancelModal={() => setShowCancelModal(false)}
        hidePackageConfirmModal={() => setShowPackageConfirmModal(false)}
        hideMissingCardConfirmModal={() => setShowMissingCardConfirmModal(false)}
        hideMissingPhoneConfirmModal={() => setShowMissingPhoneConfirmModal(false)}
        hidePastAppointmentModal={() => {
          setShowPastAppointmentModal(false)
          setPendingAction(null)
        }}
        calendarOverridePrompt={calendarOverridePrompt}
        hideCalendarOverrideModal={() => setCalendarOverridePrompt(null)}
        onCalendarOverrideConfirm={() => {
          const retry = calendarOverridePrompt?.retry
          setCalendarOverridePrompt(null)
          if (retry) retry()
        }}
        onPackageConfirm={handlePackageConfirm}
        onMissingCardConfirm={handleMissingCardConfirm}
        onMissingPhoneConfirm={handleMissingPhoneConfirm}
        onMissingPhoneRemind={handleMissingPhoneRemind}
        onPastAppointmentConfirm={handlePastAppointmentConfirm}
        practitionerNote={practitionerNote}
        setPractitionerNote={setPractitionerNote}
        cancelRecurringSeries={cancelRecurringSeries}
        setCancelRecurringSeries={setCancelRecurringSeries}
      />
    </div>
  )
}

export default ClientEventForm
